// src/components/PlaylistSelector.jsx
import React, { useEffect, useMemo, useState } from "react";
import "./PlaylistModal.css";
import { db } from "../utils/firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { playlistGuideSnapshot } from "./studioVideoGuideState";

const YOUTUBE_API_KEY = process.env.REACT_APP_GOOGLE_CLOUD_API_KEY2;

export default function PlaylistSelector({
  onPlaylistSelect,
  onGuideStateChange,
  activePlaylistId = null,
  defaultSelectedId = null,
}) {
  const { userRole } = useAuth() || {};
  const auth = getAuth();

  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // 'recent' | 'title'
  const [selectedPid, setSelectedPid] = useState(defaultSelectedId || null);
  const [newPlaylistId, setNewPlaylistId] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNote, setRefreshNote] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const snap = await getDocs(collection(db, "playlists"));
        if (cancelled) return;
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPlaylists(rows);
      } catch (e) {
        setError(e.message || "Failed to load playlists.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof onGuideStateChange !== "function") return;
    onGuideStateChange(playlistGuideSnapshot({
      loading,
      error,
      playlists,
      activePlaylistId,
    }));
  }, [loading, error, playlists, activePlaylistId, onGuideStateChange]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let rows = !q
      ? playlists
      : playlists.filter(
          (p) =>
            p.title?.toLowerCase().includes(q) ||
            p.owner?.toLowerCase().includes(q) ||
            p.playlistId?.toLowerCase().includes(q)
        );
    if (sortBy === "title") {
      rows = [...rows].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    } else {
      rows = [...rows].sort(
        (a, b) => (b.addedAt?.seconds || 0) - (a.addedAt?.seconds || 0)
      );
    }
    return rows;
  }, [playlists, query, sortBy]);

  const fetchPlaylistMeta = async (playlistId) => {
    // 1) Playlist-level details
    const plUrl = `https://www.googleapis.com/youtube/v3/playlists?part=snippet,contentDetails&id=${encodeURIComponent(playlistId)}&key=${YOUTUBE_API_KEY}`;
    const { data: plData } = await axios.get(plUrl);
    const plItem = plData.items?.[0];
    if (!plItem) throw new Error("No playlist found with that ID.");

    const plThumbs = plItem.snippet?.thumbnails || {};
    const playlistThumb =
      plThumbs.maxres?.url ||
      plThumbs.standard?.url ||
      plThumbs.high?.url ||       // 480×360
      plThumbs.medium?.url ||     // 320×180
      plThumbs.default?.url || "";

    // 2) First video in the playlist → high-res thumbs (16:9)
    const itUrl = `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${encodeURIComponent(playlistId)}&maxResults=1&key=${YOUTUBE_API_KEY}`;
    const { data: itData } = await axios.get(itUrl);
    const firstVid = itData.items?.[0]?.snippet?.resourceId?.videoId || null;

    const videoThumbs = firstVid
      ? {
          maxres: `https://i.ytimg.com/vi/${firstVid}/maxresdefault.jpg`, // 1280×720 (if available)
          sd:     `https://i.ytimg.com/vi/${firstVid}/sddefault.jpg`,     // 640×480
          hq:     `https://i.ytimg.com/vi/${firstVid}/hqdefault.jpg`,     // 480×360
          mq:     `https://i.ytimg.com/vi/${firstVid}/mqdefault.jpg`,     // 320×180
          def:    `https://i.ytimg.com/vi/${firstVid}/default.jpg`,        // 120×90
        }
      : null;

    // Prefer the video’s maxres (crisp 16:9), fall back to playlist image
    const bestThumb = videoThumbs?.maxres || playlistThumb;

    return {
      title: plItem.snippet?.title || "Untitled",
      owner: plItem.snippet?.channelTitle || "Unknown",
      videoCount: plItem.contentDetails?.itemCount || 0,
      thumbnail: bestThumb,
      thumbs: { playlist: plThumbs, video: videoThumbs, firstVideoId: firstVid }
    };
  };

  const handleAddPlaylist = async () => {
    if (userRole !== "admin") return;
    setError("");
    const trimmedId = newPlaylistId.trim();
    if (!trimmedId) return;

    if (playlists.find((p) => p.playlistId === trimmedId)) {
      setError("That playlist is already saved.");
      return;
    }

    try {
      setLoading(true);
      const meta = await fetchPlaylistMeta(trimmedId);
      await addDoc(collection(db, "playlists"), {
        playlistId: trimmedId,
        title: meta.title,
        owner: meta.owner,
        thumbnail: meta.thumbnail,
        thumbs: meta.thumbs || null,
        videoCount: meta.videoCount,
        addedAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || "unknown",
      });
      // refresh
      const snap = await getDocs(collection(db, "playlists"));
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPlaylists(rows);
      setNewPlaylistId("");
    } catch (e) {
      setError(e.message || "Failed to save playlist.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (userRole !== "admin") return;
    try {
      await deleteDoc(doc(db, "playlists", id));
      setPlaylists((prev) => prev.filter((p) => p.id !== id));
      if (selectedPid === id) setSelectedPid(null);
    } catch (e) {
      setError(e.message || "Delete failed.");
    }
  };

  const select = (p) => {
    setSelectedPid(p.playlistId);
    onPlaylistSelect && onPlaylistSelect(p.playlistId);
  };

  async function refreshCovers() {
    if (userRole !== "admin" || refreshing) return;
    setRefreshing(true);
    setRefreshNote(`Starting cover refresh for ${playlists.length} playlists…`);

    let ok = 0, fail = 0;
    for (const p of playlists) {
      try {
        const meta = await fetchPlaylistMeta(p.playlistId); // uses our upgraded fetch
        await updateDoc(doc(db, "playlists", p.id), {
          thumbnail: meta.thumbnail,
          thumbs: meta.thumbs || null,
        });
        // reflect changes locally so cards update immediately
        setPlaylists((prev) =>
          prev.map((row) => row.id === p.id
            ? { ...row, thumbnail: meta.thumbnail, thumbs: meta.thumbs || null }
            : row
          )
        );
        ok++;
        setRefreshNote(`Refreshed ${ok}/${playlists.length}…`);
      } catch (e) {
        console.error("Refresh failed for", p.playlistId, e);
        fail++;
      }
    }

    setRefreshing(false);
    setRefreshNote(`Done. ${ok} updated, ${fail} failed.`);
    // auto-clear the note after a few seconds
    setTimeout(() => setRefreshNote(""), 3500);
  }

  return (
    <div className="playlist-modal">
      <div className="playlist-panel" data-guide="studio-playlist-picker">
        <header className="playlist-header">
          <h2 className="fx-title">
            <span className="fx-ghost">The iSpeakWell Project</span>
            <span>Saved Playlists</span>
          </h2>

          <div className="selector-toolbar">
            <div className="search-wrap">
              <input
                className="fx-input"
                placeholder="Search title, channel, or ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search playlists"
              />
            </div>
            <div className="sort-wrap">
              <label className="fx-label" htmlFor="sortBy">Sort</label>
              <select
                id="sortBy"
                className="fx-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent">Most recent</option>
                <option value="title">Title (A–Z)</option>
              </select>
            </div>
          </div>

          {userRole === "admin" ? (
              <div className="admin-bar">
                <span className="badge badge-admin" title="Admin tools">Admin</span>
                <input
                  className="fx-input grow"
                  placeholder="YouTube playlist ID (e.g. PL…)"
                  value={newPlaylistId}
                  onChange={(e) => setNewPlaylistId(e.target.value)}
                  aria-label="YouTube playlist ID"
                />
                <button className="btn-primary" onClick={handleAddPlaylist} disabled={!newPlaylistId.trim()}>
                  Save
                </button>
                <button
                  className="btn-secondary"
                  onClick={refreshCovers}
                  disabled={refreshing || playlists.length === 0}
                  title="Re-fetch high-res thumbnails for all saved playlists"
                >
                  {refreshing ? "Refreshing…" : "Refresh covers"}
                </button>
              </div>
            ) : (
            <div className="admin-bar disabled" role="note" aria-label="View-only notice">
              <span className="badge badge-locked">View only</span>
              Saved by admins • pick a playlist to use.
            </div>
          )}
        </header>

        {error && <div className="toast error" role="alert">{error}</div>}
        {refreshNote && <div className="toast">{refreshNote}</div>}
        <div className={`playlist-grid ${loading ? "loading" : ""}`}>
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="playlist-card skeleton" aria-hidden="true" />
              ))
            : filtered.map((p) => (
                <div
                  key={p.id}
                  className={`playlist-card ${selectedPid === p.playlistId ? "selected" : ""}`}
                  tabIndex={0}
                  role="button"
                  onClick={() => select(p)}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && select(p)}
                >
                  <div className="thumb-wrap">
                    {p.thumbnail ? (
                      <img
                        src={p.thumbnail}
                        srcSet={
                          p.thumbs?.video
                            ? [
                                `${p.thumbs.video.mq} 320w`,
                                `${p.thumbs.video.hq} 480w`,
                                `${p.thumbs.video.sd} 640w`,
                                `${p.thumbs.video.maxres} 1280w`,
                              ].join(", ")
                            : (p.thumbs?.playlist?.high || p.thumbnail) + " 480w"
                        }
                        sizes="(min-width: 1100px) 320px, (min-width: 700px) 33vw, 100vw"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          // Robust step-down through known URLs without looping
                          const v = p.thumbs?.video;
                          if (!v) return;
                          const chain = [v.maxres, v.sd, v.hq, v.mq, v.def].filter(Boolean);
                          const i = parseInt(e.currentTarget.dataset.i || "0", 10);
                          const next = chain[i + 1];
                          if (next) {
                            e.currentTarget.src = next;
                            e.currentTarget.dataset.i = String(i + 1);
                          }
                        }}
                        alt="Playlist Video for {p.title}"
                      />
                    ) : (
                        <div className="thumb-fallback" aria-hidden="true">No Art</div>
                    )}
                    <span className="chip count">{p.videoCount} video{p.videoCount === 1 ? "" : "s"}</span>
                    {selectedPid === p.id && (
                      <span className="checkmark" aria-hidden="true">
                        {/* check icon (inline SVG) */}
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className="info">
                    <h3 className="title" title={p.title}>{p.title}</h3>
                    <p className="owner" title={p.owner}>{p.owner}</p>
                  </div>

                  {userRole === "admin" && (
                    <button
                      className="icon-btn danger icon-bottom-right"
                      title="Delete playlist"
                      aria-label="Delete playlist"
                      onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }}
                    >
                      {/* centered, complete trashcan */}
                      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                        <path d="M3 6h18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 6v12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M16 6v12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M10 6V4h4v2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <rect x="6" y="6" width="12" height="14" rx="1.5" ry="1.5" fill="none" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  )}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
